import type { ScrollContainerProps } from '@carboncopyco/ui';
import {
  ErrorBox,
  LoadingDots,
  ScrollContainer,
  SkeletonRowLoading,
} from '@carboncopyco/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type React from 'react';

export type InfiniteListProps = ScrollContainerProps & {
  loaderPlaceholderSpace?: number;
} & (
    | {
        isLoading: boolean;
        loader: React.ReactElement;
      }
    | {
        isLoading?: boolean;
        loader?: never;
      }
  ) &
  (
    | {
        isLoadingBatch: boolean;
        batchLoader: React.ReactElement;
      }
    | {
        isLoadingBatch?: boolean;
        batchLoader?: never;
      }
  ) &
  (
    | {
        error: SerializedError;
        errorComponent: React.ReactElement;
      }
    | {
        error?: SerializedError;
        errorComponent?: never;
      }
  );

export const InfiniteList = ({
  error,
  errorComponent,
  children,
  onReachBottom,
  loader,
  batchLoader,
  isLoading,
  isLoadingBatch,
  bottomOffset,
  loaderPlaceholderSpace = 96,
}: InfiniteListProps) => {
  if (error) {
    return (
      errorComponent || (
        <ErrorBox
          contained
          title={error.message}
        />
      )
    );
  }

  return (
    <ScrollContainer
      bottomOffset={bottomOffset}
      onReachBottom={onReachBottom}
    >
      {isLoading ? (
        loader || <SkeletonRowLoading itemsCount={10} />
      ) : (
        <div className="h-full flex flex-col">
          {children}
          <div
            className="flex justify-between items-stretch"
            style={{
              height: `${loaderPlaceholderSpace}px`,
            }}
          >
            {isLoadingBatch &&
              (batchLoader || (
                <div className="flex flex-grow justify-center items-center">
                  <LoadingDots />
                </div>
              ))}
          </div>
        </div>
      )}
    </ScrollContainer>
  );
};
